import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, Youtube } from "lucide-react";
import { useTranslation } from "react-i18next";
import { WizardLogo } from "../branding/WizardLogo";
import { PortalDomain } from "../../types/branding.types";
import { SocialLink } from "../../constants/social-links";

const XIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const MediumIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zm7.42 0c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42 3.38 2.88 3.38 6.42M24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.19-2.58-1.19-5.75s.53-5.75 1.19-5.75C23.47 6.25 24 8.83 24 12z" />
  </svg>
);

const RedditIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 0C5.373 0 0 5.373 0 12c0 3.314 1.343 6.314 3.515 8.485l-2.286 2.286C.775 23.225 1.097 24 1.738 24H12c6.627 0 12-5.373 12-12S18.627 0 12 0zm5.492 13.905c.064.267.096.543.096.826 0 2.993-3.037 5.419-6.784 5.419-3.747 0-6.784-2.426-6.784-5.42 0-.282.032-.558.096-.825a1.79 1.79 0 01-.554-1.293c0-.988.806-1.789 1.8-1.789.479 0 .915.19 1.234.5 1.067-.706 2.434-1.138 3.94-1.194l.807-3.665a.389.389 0 01.468-.293l2.695.573a1.241 1.241 0 012.197.97 1.244 1.244 0 01-2.39.475l-2.317-.493-.683 3.098c1.47.07 2.8.5 3.843 1.19a1.79 1.79 0 011.227-.494 1.79 1.79 0 011.8 1.789c0 .5-.207.955-.542 1.283zm-8.87 1.28a1.241 1.241 0 100-2.483 1.241 1.241 0 000 2.483zm5.413 1.993c.165-.165.165-.433 0-.598a.423.423 0 00-.598 0c-.527.527-1.398.742-2.436.742-1.037 0-1.909-.215-2.436-.742a.423.423 0 00-.598 0c-.165.165-.165.433 0 .598.738.738 1.888 1.024 3.034 1.024 1.147 0 2.296-.286 3.034-1.024zm-.617-1.993a1.241 1.241 0 100-2.483 1.241 1.241 0 000 2.483z" />
  </svg>
);

const SOCIAL_ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  linkedin: ({ className }) => <Linkedin className={className} />,
  x: XIcon,
  medium: MediumIcon,
  youtube: ({ className }) => <Youtube className={className} />,
  reddit: RedditIcon,
};

export interface FooterLink {
  name: string;
  href: string;
}

export interface FooterProps {
  domain?: PortalDomain;
  companyDescription?: string;
  quickLinks?: FooterLink[];
  email?: string;
  phone?: string;
  address?: string;
  linkedinUrl?: string;
  socialLinks?: SocialLink[];
}

export const Footer: React.FC<FooterProps> = ({
  domain = "main",
  companyDescription,
  quickLinks = [
    { name: "Home", href: "/" },
    { name: "About Us", href: "/about" },
    { name: "Contact", href: "/contact" },
  ],
  email = "admin@olorin.ai",
  phone,
  address,
  linkedinUrl = "https://www.linkedin.com/company/olorin-ai-llc",
  socialLinks,
}) => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const description =
    companyDescription ??
    t("footer.companyDescription", {
      defaultValue:
        "Leading the future of enterprise AI with Generative AI agentic solutions across fraud detection, media streaming, and radio management.",
    });

  return (
    <footer className="glass-footer-wizard">
      <div className="wizard-container py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="mb-4 inline-block hover-scale">
              <WizardLogo variant={domain} size="md" showText />
            </Link>
            <p className="text-wizard-text-secondary mb-6 max-w-md">
              {description}
            </p>
            <div className="flex space-x-4">
              {socialLinks && socialLinks.length > 0 ? (
                socialLinks.map((link) => {
                  const IconComponent = SOCIAL_ICON_MAP[link.platform];
                  return (
                    <a
                      key={link.platform}
                      href={link.url}
                      className="text-wizard-text-secondary hover:text-wizard-accent-purple hover:glow-purple transition-all duration-300"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={link.label}
                    >
                      {IconComponent && <IconComponent className="h-5 w-5" />}
                    </a>
                  );
                })
              ) : (
                <a
                  href={linkedinUrl}
                  className="text-wizard-text-secondary hover:text-wizard-accent-purple hover:glow-purple transition-all duration-300"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="h-5 w-5" />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-wizard-text-primary">
              {t("footer.quickLinks", { defaultValue: "Quick Links" })}
            </h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-wizard-text-secondary hover:text-wizard-accent-purple transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-wizard-text-primary">
              {t("footer.contactInfo", { defaultValue: "Contact Info" })}
            </h3>
            <ul className="space-y-3">
              <li className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-wizard-accent-purple glow-icon" />
                <a
                  href={`mailto:${email}`}
                  className="text-wizard-text-secondary hover:text-wizard-accent-purple transition-colors"
                >
                  {email}
                </a>
              </li>
              {phone && (
                <li className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-wizard-accent-purple glow-icon" />
                  <a
                    href={`tel:${phone.replace(/[^0-9+]/g, "")}`}
                    className="text-wizard-text-secondary hover:text-wizard-accent-purple transition-colors"
                  >
                    {phone}
                  </a>
                </li>
              )}
              {address && (
                <li className="flex items-start space-x-2">
                  <MapPin className="h-4 w-4 text-wizard-accent-purple glow-icon mt-0.5" />
                  <span className="text-wizard-text-secondary whitespace-pre-line">
                    {address}
                  </span>
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-wizard-border-secondary mt-8 pt-8 flex flex-col sm:flex-row justify-between items-center">
          <p className="text-wizard-text-muted text-sm">
            {t("footer.copyright", {
              defaultValue: "© {{year}} Olorin.ai. All rights reserved.",
              year: currentYear,
            })}
          </p>
          <div className="flex space-x-4 mt-4 sm:mt-0">
            <Link
              to="/privacy"
              className="text-wizard-text-muted hover:text-wizard-accent-purple text-sm transition-colors"
            >
              {t("footer.privacyPolicy", { defaultValue: "Privacy Policy" })}
            </Link>
            <Link
              to="/terms"
              className="text-wizard-text-muted hover:text-wizard-accent-purple text-sm transition-colors"
            >
              {t("footer.termsOfService", { defaultValue: "Terms of Service" })}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
